import Image from '@tiptap/extension-image';
import { mergeAttributes } from '@tiptap/core';

import { imageCropFrameStyle, imageCropImgStyle, imageCropLayout, serializeImageCrop, normalizeImageCrop } from './image-crop.util';

export interface AttachmentImageInsertPayload {
  attachmentId: string;
  src?: string;
  url?: string;
  /** Attachment metadata, not stored in the node — lets the host list the attachment right away. */
  filename?: string;
  fileSizeBytes?: number;
  mimeType?: string;
}

export interface LinkedImageInsertPayload {
  url: string;
  /** Measured natural width of the remote image (px). */
  naturalWidth?: number | null;
  /** Measured natural height of the remote image (px). */
  naturalHeight?: number | null;
  /** Initial display width to apply (px, capped to 800). */
  width?: number | null;
  /** Initial display height to apply (px, aspect-ratio preserved). */
  height?: number | null;
}

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return fallback;
};

const normalizeCssDimension = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return `${value}px`;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    return `${trimmed}px`;
  }

  if (/^\d+(?:\.\d+)?(px|%|em|rem|vw|vh)$/.test(trimmed)) {
    return trimmed;
  }

  return null;
};

const extractStyleDimension = (element: HTMLElement, property: 'width' | 'height'): string | null => {
  // A cropped image's display size is on its clipping frame; the image itself is sized in % of it.
  const frame = element.parentElement;
  if (element.hasAttribute('data-crop') && frame?.classList.contains('image-crop')) {
    return frame.style.getPropertyValue(property)?.trim() || null;
  }

  const inlineValue = element.style.getPropertyValue(property)?.trim();
  if (inlineValue) {
    return inlineValue;
  }

  const styleAttr = element.getAttribute('style') || '';
  const match = styleAttr.match(new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() || null;
};

const parseDimensionToPx = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const plain = Number(trimmed);
  if (Number.isFinite(plain) && plain > 0) {
    return plain;
  }

  const pxMatch = trimmed.match(/^(\d+(?:\.\d+)?)px$/i);
  if (pxMatch) {
    const parsed = Number(pxMatch[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
};

/** A border color safe to put in an inline style (hex or rgb[a]()); anything else means no border. */
export const normalizeImageBorderColor = (value: unknown): string | null => {
  const trimmed = String(value ?? '').trim();
  if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\)$/i.test(trimmed)) {
    return trimmed.replace(/\s+/g, '');
  }
  return null;
};

/** Picture border: 2px, solid. Images saved with the earlier 1px border get 2px when next rendered or saved. */
export const IMAGE_BORDER_WIDTH = '2px';
export const imageBorderCss = (color: string): string => `${IMAGE_BORDER_WIDTH} solid ${color}`;

type ImageHandleDirection = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    phase1Image: {
      insertLinkedImage: (payload: LinkedImageInsertPayload) => ReturnType;
      /** Inserts at the selection, or at `position` (without moving the selection) when given. */
      insertAttachmentImage: (attachmentResponse: AttachmentImageInsertPayload, position?: number) => ReturnType;
    };
  }
}

export const Phase1Image = Image.extend({
  name: 'image',

  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentNode = node;

      const MIN_WIDTH = 50;
      const MIN_HEIGHT = 50;

      const wrapper = document.createElement('div');
      wrapper.className = 'tiptap-image-node';
      wrapper.contentEditable = 'false';

      const img = document.createElement('img');
      img.className = 'tiptap-image-node__img';
      img.draggable = false;
      img.setAttribute('draggable', 'false');
      wrapper.appendChild(img);

      // Only while the image is cropped: the frame that clips it (see image-crop.util.ts).
      let frame: HTMLSpanElement | null = null;
      /** The box the user sees and resizes: the crop frame, or the image itself. */
      const sizeBox = (): HTMLElement => frame ?? img;

      const handleElements: HTMLSpanElement[] = [];
      const handleDirections: ImageHandleDirection[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
      const handleListeners: Array<{ el: HTMLElement; type: string; fn: EventListener }> = [];
      const addHandleListener = (el: HTMLElement, type: string, fn: EventListener): void => {
        el.addEventListener(type, fn);
        handleListeners.push({ el, type, fn });
      };

      for (const direction of handleDirections) {
        const handle = document.createElement('span');
        handle.className = `tiptap-image-node__handle tiptap-image-node__handle--${direction}`;
        handle.setAttribute('data-handle', direction);
        handle.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(handle);
        handleElements.push(handle);
      }

      const applyImageDomFromNode = (targetNode: typeof currentNode): void => {
        const syncAttr = (name: string, value: unknown): void => {
          const normalized = String(value ?? '').trim();
          if (normalized) {
            img.setAttribute(name, normalized);
          } else {
            img.removeAttribute(name);
          }
        };

        const src = String(targetNode.attrs['src'] ?? '').trim();
        if (img.getAttribute('src') !== src) {
          img.setAttribute('src', src);
        }

        // Preserve custom image metadata on the DOM node so existing editor-level
        // attachment hydration logic can resolve authenticated blobs.
        syncAttr('data-image-source-type', targetNode.attrs['imageSourceType']);
        syncAttr('data-attachment-id', targetNode.attrs['attachmentId']);
        syncAttr('data-original-url', targetNode.attrs['originalUrl']);
        syncAttr('data-natural-width', targetNode.attrs['naturalWidth']);
        syncAttr('data-natural-height', targetNode.attrs['naturalHeight']);
        img.setAttribute('data-keep-aspect-ratio', toBoolean(targetNode.attrs['keepAspectRatio'], true) ? 'true' : 'false');

        const alt = targetNode.attrs['alt'];
        if (typeof alt === 'string') {
          img.setAttribute('alt', alt);
        } else {
          img.removeAttribute('alt');
        }

        const title = targetNode.attrs['title'];
        if (typeof title === 'string') {
          img.setAttribute('title', title);
        } else {
          img.removeAttribute('title');
        }

        const widthPx = parseDimensionToPx(targetNode.attrs['width']);
        const heightPx = parseDimensionToPx(targetNode.attrs['height']);
        const keepAspectRatio = toBoolean(targetNode.attrs['keepAspectRatio'], true);
        const borderColor = normalizeImageBorderColor(targetNode.attrs['borderColor']);
        syncAttr('data-border-color', borderColor);
        syncAttr('data-crop', serializeImageCrop(targetNode.attrs['crop']));

        const cropLayout = imageCropLayout(targetNode.attrs['crop'], targetNode.attrs['naturalWidth'], targetNode.attrs['naturalHeight']);
        if (cropLayout) {
          if (!frame) {
            frame = document.createElement('span');
            frame.className = 'image-crop';
            wrapper.insertBefore(frame, img);
            frame.appendChild(img);
          }
          frame.style.cssText = imageCropFrameStyle(
            cropLayout,
            widthPx ? `${Math.round(widthPx)}px` : null,
            !keepAspectRatio && heightPx ? `${Math.round(heightPx)}px` : null,
            borderColor ? imageBorderCss(borderColor) : null,
          );
          img.style.cssText = imageCropImgStyle(cropLayout);
          img.classList.add('image-crop__img');
          return;
        }

        if (frame) {
          wrapper.insertBefore(img, frame);
          frame.remove();
          frame = null;
          img.classList.remove('image-crop__img');
          img.removeAttribute('style');
        }
        img.style.maxWidth = '100%';
        img.style.width = widthPx ? `${Math.round(widthPx)}px` : '';
        img.style.height = keepAspectRatio ? 'auto' : (heightPx ? `${Math.round(heightPx)}px` : '');
        img.style.border = borderColor ? imageBorderCss(borderColor) : '';
      };

      const preventDrag = (event: Event): void => {
        event.preventDefault();
      };
      img.addEventListener('dragstart', preventDrag);

      const getMaxWidthPx = (): number => {
        const parent = wrapper.parentElement as HTMLElement | null;
        if (!parent) {
          return Number.POSITIVE_INFINITY;
        }
        return Math.max(MIN_WIDTH, parent.clientWidth || MIN_WIDTH);
      };

      let activePointerId: number | null = null;
      let cleanupResizeEvents: (() => void) | null = null;

      const endResize = (): void => {
        if (cleanupResizeEvents) {
          cleanupResizeEvents();
          cleanupResizeEvents = null;
        }
        activePointerId = null;
        wrapper.classList.remove('is-resizing');
        document.body.classList.remove('tiptap-image-resizing');
        document.body.style.userSelect = '';
      };

      const startResize = (event: PointerEvent, direction: ImageHandleDirection): void => {
        if (!editor.isEditable) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const target = event.currentTarget as HTMLElement | null;
        if (!target) {
          return;
        }

        activePointerId = event.pointerId;
        target.setPointerCapture?.(event.pointerId);

        const startX = event.clientX;
        const startY = event.clientY;
        const box = sizeBox();
        const startRect = box.getBoundingClientRect();
        const startWidth = Math.max(MIN_WIDTH, startRect.width || parseDimensionToPx(currentNode.attrs['width']) || MIN_WIDTH);
        const startHeight = Math.max(MIN_HEIGHT, startRect.height || parseDimensionToPx(currentNode.attrs['height']) || MIN_HEIGHT);
        const aspectRatio = (() => {
          const cropLayout = imageCropLayout(currentNode.attrs['crop'], currentNode.attrs['naturalWidth'], currentNode.attrs['naturalHeight']);
          if (cropLayout) {
            return cropLayout.aspectRatio;
          }
          const nw = parseDimensionToPx(currentNode.attrs['naturalWidth']);
          const nh = parseDimensionToPx(currentNode.attrs['naturalHeight']);
          if (nw && nh) {
            return nw / nh;
          }
          if (startWidth > 0 && startHeight > 0) {
            return startWidth / startHeight;
          }
          return 1;
        })();
        const keepAspectRatio = toBoolean(currentNode.attrs['keepAspectRatio'], true);

        wrapper.classList.add('is-resizing');
        document.body.classList.add('tiptap-image-resizing');
        document.body.style.userSelect = 'none';

        const onPointerMove = (moveEvent: PointerEvent): void => {
          if (activePointerId === null || moveEvent.pointerId !== activePointerId) {
            return;
          }

          moveEvent.preventDefault();

          const dx = moveEvent.clientX - startX;
          const dy = moveEvent.clientY - startY;

          const widthAxis = direction.includes('e') ? 1 : (direction.includes('w') ? -1 : 0);
          const heightAxis = direction.includes('s') ? 1 : (direction.includes('n') ? -1 : 0);

          let nextWidth = startWidth;
          let nextHeight = startHeight;

          if (widthAxis !== 0) {
            nextWidth = startWidth + dx * widthAxis;
          }
          if (heightAxis !== 0) {
            nextHeight = startHeight + dy * heightAxis;
          }

          if (keepAspectRatio) {
            if (widthAxis !== 0 && heightAxis !== 0) {
              const horizontalDelta = dx * widthAxis;
              const verticalDelta = dy * heightAxis;

              if (Math.abs(horizontalDelta) >= Math.abs(verticalDelta)) {
                nextWidth = startWidth + horizontalDelta;
                nextHeight = nextWidth / aspectRatio;
              } else {
                nextHeight = startHeight + verticalDelta;
                nextWidth = nextHeight * aspectRatio;
              }
            } else if (widthAxis !== 0) {
              nextHeight = nextWidth / aspectRatio;
            } else if (heightAxis !== 0) {
              nextWidth = nextHeight * aspectRatio;
            } else {
              nextWidth = startWidth;
              nextHeight = startHeight;
            }
          }

          const maxWidth = getMaxWidthPx();
          nextWidth = Math.max(MIN_WIDTH, Math.min(nextWidth, maxWidth));

          if (keepAspectRatio) {
            nextHeight = nextWidth / aspectRatio;
          }

          nextHeight = Math.max(MIN_HEIGHT, nextHeight);

          if (keepAspectRatio) {
            nextWidth = Math.max(MIN_WIDTH, Math.min(nextHeight * aspectRatio, maxWidth));
          }

          box.style.width = `${Math.round(nextWidth)}px`;
          if (box === img) {
            img.style.height = keepAspectRatio ? 'auto' : `${Math.round(nextHeight)}px`;
          } else if (!keepAspectRatio) {
            box.style.height = `${Math.round(nextHeight)}px`;
          }
        };

        const onPointerUpOrCancel = (upEvent: PointerEvent): void => {
          if (activePointerId === null || upEvent.pointerId !== activePointerId) {
            return;
          }

          upEvent.preventDefault();

          const pos = typeof getPos === 'function' ? getPos() : null;
          if (typeof pos === 'number') {
            const finalRect = box.getBoundingClientRect();
            const widthToSave = Math.max(MIN_WIDTH, Math.round(finalRect.width));
            const heightToSave = Math.max(MIN_HEIGHT, Math.round(finalRect.height));
            const keepAspectRatio = toBoolean(currentNode.attrs['keepAspectRatio'], true);

            editor.chain().focus().command(({ tr, dispatch }) => {
              const existing = tr.doc.nodeAt(pos);
              if (!existing || existing.type.name !== 'image') {
                return false;
              }

              tr.setNodeMarkup(pos, undefined, {
                ...existing.attrs,
                width: `${widthToSave}px`,
                height: keepAspectRatio ? null : `${heightToSave}px`,
              });

              if (dispatch) {
                dispatch(tr);
              }
              return true;
            }).run();
          }

          endResize();
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUpOrCancel);
        window.addEventListener('pointercancel', onPointerUpOrCancel);
        cleanupResizeEvents = () => {
          window.removeEventListener('pointermove', onPointerMove);
          window.removeEventListener('pointerup', onPointerUpOrCancel);
          window.removeEventListener('pointercancel', onPointerUpOrCancel);
          if (target.hasPointerCapture?.(event.pointerId)) {
            target.releasePointerCapture(event.pointerId);
          }
        };
      };

      for (const handle of handleElements) {
        const onPointerDown: EventListener = (rawEvent: Event) => {
          startResize(rawEvent as PointerEvent, handle.dataset['handle'] as ImageHandleDirection);
        };
        addHandleListener(handle, 'pointerdown', onPointerDown);
      }

      applyImageDomFromNode(currentNode);

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) {
            return false;
          }

          currentNode = updatedNode;
          applyImageDomFromNode(updatedNode);
          return true;
        },
        selectNode: () => {
          wrapper.classList.add('is-selected');
        },
        deselectNode: () => {
          wrapper.classList.remove('is-selected');
          endResize();
        },
        ignoreMutation: (mutation) => {
          const target = mutation.target;
          return target instanceof Node && (wrapper.contains(target) || target === wrapper);
        },
        destroy: () => {
          endResize();
          img.removeEventListener('dragstart', preventDrag);
          for (const listener of handleListeners) {
            listener.el.removeEventListener(listener.type, listener.fn);
          }
        },
      };
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => extractStyleDimension(element as HTMLElement, 'width') || (element as HTMLElement).getAttribute('width'),
        renderHTML: () => ({}),
      },
      height: {
        default: null,
        parseHTML: (element) => extractStyleDimension(element as HTMLElement, 'height') || (element as HTMLElement).getAttribute('height'),
        renderHTML: () => ({}),
      },
      keepAspectRatio: {
        default: true,
        parseHTML: (element) => toBoolean((element as HTMLElement).getAttribute('data-keep-aspect-ratio'), true),
        renderHTML: (attributes) => ({
          'data-keep-aspect-ratio': attributes['keepAspectRatio'] === false ? 'false' : 'true',
        }),
      },
      imageSourceType: {
        default: null,
        parseHTML: (element) => (element as HTMLElement).getAttribute('data-image-source-type'),
        renderHTML: (attributes) => {
          const value = attributes['imageSourceType'];
          return value ? { 'data-image-source-type': value } : {};
        },
      },
      attachmentId: {
        default: null,
        parseHTML: (element) => (element as HTMLElement).getAttribute('data-attachment-id'),
        renderHTML: (attributes) => {
          const value = attributes['attachmentId'];
          return value ? { 'data-attachment-id': value } : {};
        },
      },
      originalUrl: {
        default: null,
        parseHTML: (element) => (element as HTMLElement).getAttribute('data-original-url'),
        renderHTML: (attributes) => {
          const value = attributes['originalUrl'];
          return value ? { 'data-original-url': value } : {};
        },
      },
      naturalWidth: {
        default: null,
        parseHTML: (element) => {
          const v = (element as HTMLElement).getAttribute('data-natural-width');
          return v ? toPositiveNumber(v) : null;
        },
        renderHTML: (attributes) => {
          const v = toPositiveNumber(attributes['naturalWidth']);
          return v ? { 'data-natural-width': String(v) } : {};
        },
      },
      naturalHeight: {
        default: null,
        parseHTML: (element) => {
          const v = (element as HTMLElement).getAttribute('data-natural-height');
          return v ? toPositiveNumber(v) : null;
        },
        renderHTML: (attributes) => {
          const v = toPositiveNumber(attributes['naturalHeight']);
          return v ? { 'data-natural-height': String(v) } : {};
        },
      },
      borderColor: {
        default: null,
        parseHTML: (element) => normalizeImageBorderColor((element as HTMLElement).getAttribute('data-border-color')),
        renderHTML: (attributes) => {
          const value = normalizeImageBorderColor(attributes['borderColor']);
          return value ? { 'data-border-color': value } : {};
        },
      },
      crop: {
        default: null,
        parseHTML: (element) => normalizeImageCrop((element as HTMLElement).getAttribute('data-crop')),
        renderHTML: (attributes) => {
          const value = serializeImageCrop(attributes['crop']);
          return value ? { 'data-crop': value } : {};
        },
      },
      // NOTE: fileSizeBytes is attachment metadata and belongs only in the
      // IndexedDB attachment record, not in the Tiptap node.
    };
  },

  renderHTML({ node, HTMLAttributes }: { node: { attrs: Record<string, unknown> }; HTMLAttributes: Record<string, unknown> }) {
    // width/height attrs use renderHTML: () => ({}) so they are absent from HTMLAttributes.
    // Read them directly from the node to produce the correct inline style.
    const width = normalizeCssDimension(node.attrs['width']);
    const height = normalizeCssDimension(node.attrs['height']);
    const keepAspectRatio = toBoolean(node.attrs['keepAspectRatio'], true);
    const styleParts = ['max-width: 100%'];

    if (width) {
      styleParts.push(`width: ${width}`);
    }
    if (!keepAspectRatio && height) {
      styleParts.push(`height: ${height}`);
    } else {
      styleParts.push('height: auto');
    }
    const borderColor = normalizeImageBorderColor(node.attrs['borderColor']);
    if (borderColor) {
      styleParts.push(`border: ${imageBorderCss(borderColor)}`);
    }

    const mergedAttributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
      style: styleParts.join('; '),
    });

    delete mergedAttributes['width'];
    delete mergedAttributes['height'];

    // Cropped: the whole image inside a frame that shows only the cropped part. Also how it looks
    // wherever the saved HTML is shown outside the editor.
    const cropLayout = imageCropLayout(node.attrs['crop'], node.attrs['naturalWidth'], node.attrs['naturalHeight']);
    if (cropLayout) {
      const frameStyle = imageCropFrameStyle(
        cropLayout,
        width,
        !keepAspectRatio && height ? height : null,
        borderColor ? imageBorderCss(borderColor) : null,
      );
      const imgClass = [mergedAttributes['class'], 'image-crop__img'].filter(Boolean).join(' ');
      return ['span', { class: 'image-crop', style: frameStyle }, ['img', { ...mergedAttributes, class: imgClass, style: imageCropImgStyle(cropLayout) }]];
    }

    return ['img', mergedAttributes];
  },

  addCommands() {
    return {
      ...this.parent?.(),
      insertLinkedImage:
        (payload: LinkedImageInsertPayload) =>
        ({ commands }) => {
          const safeUrl = String(payload?.url ?? '').trim();
          if (!safeUrl) {
            return false;
          }

          const attrs: Record<string, unknown> = {
            src: safeUrl,
            imageSourceType: 'linked',
            originalUrl: safeUrl,
            keepAspectRatio: true,
          };

          const nw = toPositiveNumber(payload.naturalWidth);
          const nh = toPositiveNumber(payload.naturalHeight);
          if (nw) attrs['naturalWidth'] = nw;
          if (nh) attrs['naturalHeight'] = nh;

          const w = toPositiveNumber(payload.width);
          const h = toPositiveNumber(payload.height);
          if (w) attrs['width'] = w;
          if (h && attrs['keepAspectRatio'] === false) attrs['height'] = h;

          return commands.insertContent({ type: this.name, attrs });
        },
      insertAttachmentImage:
        (attachmentResponse: AttachmentImageInsertPayload, position?: number) =>
        ({ commands }) => {
          const attachmentId = String(attachmentResponse?.attachmentId ?? '').trim();
          const src = String(attachmentResponse?.src ?? attachmentResponse?.url ?? '').trim();
          if (!attachmentId || !src) {
            return false;
          }

          const content = {
            type: this.name,
            attrs: {
              src,
              imageSourceType: 'attachment',
              attachmentId,
              keepAspectRatio: true,
            },
          };
          return typeof position === 'number'
            ? commands.insertContentAt(position, content, { updateSelection: false })
            : commands.insertContent(content);
        },
    };
  },
});
